import React, { ReactNode } from 'react';

// Design System Components
import Button from 'Components/DesignSystem/Button/Button';
import Tag from 'Components/DesignSystem/Tag/Tag';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Typography from 'Components/DesignSystem/Typography/Typography';

// Constants
import { STRINGS, getApplication } from '../../constants';

// i18n
import { interpolateNodes } from '../../i18n';

// Types
import { ApplicationRef } from '../../types';

interface BlockedDeleteDialogProps {
  isOpen: boolean;
  cardName: string;
  blockers: ApplicationRef[];
  onDismiss: () => void;
}

/**
 * The sentence's tail — its last interpolated value and whatever punctuation
 * follows it — is kept on one line.
 *
 * A `Tag` is an atomic inline box, and the boundary between one and the text
 * after it is a wrap opportunity. With the badge landing at the end of a full
 * line, that put the sentence's full stop alone on the next line. The break
 * sits between two children of the paragraph, so only an element containing
 * BOTH can forbid it — `nowrap` on the badge alone is no use, and on the
 * paragraph would stop the sentence wrapping at all. A U+2060 WORD JOINER
 * between them was tried first and Chrome broke there anyway.
 *
 * Safe against overflow because the run is bounded: `APPLICATIONS` has exactly
 * three entries with one-word labels (~240px all together, in a `size="md"`
 * dialog whose content box is ~448px), so the group always has room on a line
 * of its own.
 *
 * Only jsdom's blindness to line breaking keeps this out of the unit tests —
 * it was found by rendering the dialog against the real stylesheet.
 */
const withUnbreakableTail = (segments: ReactNode[]): ReactNode[] => {
  const lastValue = segments.map((segment) => React.isValidElement(segment)).lastIndexOf(true);
  if (lastValue < 0) {
    return segments;
  }
  return [
    ...segments.slice(0, lastValue),
    <span key="sentence-tail" className="tw-whitespace-nowrap">
      {segments.slice(lastValue)}
    </span>,
  ];
};

/**
 * A card that an application still references cannot be deleted: this dialog
 * names every referencing application and offers only a dismiss action — there is
 * no destructive path out of it (spec R25).
 *
 * ## The body is a sentence, not a stack (2026-09-29)
 *
 * It used to be three children of a `flex-col tw-gap-3`: the card name, the bare
 * phrase "is currently being used by", and the badge row. Each took a full line,
 * so the dialog read as a broken list with a dangling preposition and no full
 * stop. They are now one `<p>` that flows — the name and the badges are
 * interpolated INTO the message (`STRINGS.blockedDeleteDialog.body`) rather than
 * sat beside it, which is also what keeps English word order out of this file.
 *
 * `leading-7` on that paragraph, not the default 20px: a `Tag` is a 28px pill, so
 * a sentence that wraps around one would otherwise open a ragged gap on the line
 * the badge lands on. Matching the line box to the pill keeps the wrap even.
 */
const BlockedDeleteDialog = ({
  isOpen,
  cardName,
  blockers,
  onDismiss,
}: BlockedDeleteDialogProps) => (
  <Dialog
    isOpen={isOpen}
    onClose={onDismiss}
    title={STRINGS.blockedDeleteDialog.title}
    size="md"
    footer={
      <div className="tw-flex tw-justify-end">
        <Button variant="secondary" showIcon={false} onClick={onDismiss}>
          {STRINGS.blockedDeleteDialog.dismiss}
        </Button>
      </div>
    }
  >
    <div className="tw-flex tw-flex-col tw-gap-2">
      <Typography
        variant="body-primary"
        as="p"
        className="tw-text-ink-secondary tw-leading-7"
        data-testid="product-cards-blocked-delete-body"
      >
        {withUnbreakableTail(
          interpolateNodes(STRINGS.blockedDeleteDialog.body, {
            cardName: (
              /* `as="span"` so the emphasis stays INSIDE the sentence — an `<h4>`
                 or a `<p>` here would break the line again, in the DOM this time. */
              <Typography variant="title-header" as="span" className="tw-text-ink tw-break-words">
                {cardName}
              </Typography>
            ),
            applications: blockers.map((blocker, index) => {
              const application = getApplication(blocker);
              return (
                <React.Fragment key={application.id}>
                  {/* A real space, so two badges read as two words. */}
                  {index > 0 ? ' ' : null}
                  <Tag
                    label={application.label}
                    tone={application.tone}
                    showToneGlyph
                    /* Centres the pill on the text's own middle; an inline-flex box
                       otherwise sits on the baseline and rides low in the line. */
                    className="tw-align-middle"
                  />
                </React.Fragment>
              );
            }),
          })
        )}
      </Typography>

      <Typography variant="body-primary" as="p" className="tw-text-ink-tertiary">
        {blockers.length > 1
          ? STRINGS.blockedDeleteDialog.guidanceMultiple
          : STRINGS.blockedDeleteDialog.guidanceSingle}
      </Typography>
    </div>
  </Dialog>
);

export default BlockedDeleteDialog;
